import React, { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { canDeleteField, useDeleteMultipleFields, useTranslation } from "utils";

import { GET_FIELDS } from "consts";

import { CarbonProgram, CarbonProgramOutcomesResponse, FieldListField } from "models";

import { Button, Drawer, Modal, Message as message } from "components";

import { FieldSelectionList } from "pages/app/fields/components/FieldSelectionList";

type FieldDeletionDrawerProps = {
  fields: FieldListField[];
  visible: boolean;
  onClose: () => void;
  onDelete: () => void;
  carbonProgramOutcomesResponse?: CarbonProgramOutcomesResponse;
  carbonProgram?: CarbonProgram | null;
};

export const FieldDeletionDrawer = ({ fields, visible, onClose, onDelete, carbonProgramOutcomesResponse, carbonProgram }: FieldDeletionDrawerProps) => {
  const { t } = useTranslation("farm");
  const queryClient = useQueryClient();

  const { mutateAsync: deleteMultipleFieldsAsync } = useDeleteMultipleFields();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const [showDeletionConfirmationPopover, setShowDeletionConfirmationPopover] = useState<boolean>(false);

  const handleOnClose = () => {
    setSelectedFields([]);
    setLoading(false);
    onClose();
  };

  const handleFieldDeletion = async () => {
    setLoading(true);

    await deleteMultipleFieldsAsync({
      fields: selectedFields,
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [GET_FIELDS] });
        onDelete();
      })
      .finally(() => {
        message({ content: t("fieldDeletionDrawer.fieldDeleted") });
        handleOnClose();
      });
  };

  return (
    <>
      <Drawer
        className="field-deletion-drawer"
        footer={
          <Button block disabled={loading || !selectedFields.length} loading={loading} onClick={() => setShowDeletionConfirmationPopover(true)} type="primary">
            {t("fieldDeletionDrawer.deleteFields")}
          </Button>
        }
        title={t("fieldDeletionDrawer.title")}
        visible={visible}
        onClose={handleOnClose}
      >
        <h2 className="margin-bottom-200">{t("fieldDeletionDrawer.explainer")}</h2>

        <FieldSelectionList
          fields={fields}
          isFieldSelectable={(field) => canDeleteField({ carbonProgramResult: carbonProgramOutcomesResponse?.[field.id], carbonProgram })}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
        />

        <div className="margin-bottom-250" />
        <Modal
          cancelText={t("farm:fieldDeletionDrawer.cancel")}
          description={t("farm:fieldDeletionDrawer.deleteExplainer")}
          okText={t("farm:fieldDeletionDrawer.deleteFields")}
          onCancel={() => setShowDeletionConfirmationPopover(false)}
          onOk={handleFieldDeletion}
          title={t("farm:fieldDeletionDrawer.title")}
          showModal={showDeletionConfirmationPopover}
        />
      </Drawer>
    </>
  );
};
